import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  InputAdornment,
  Grid,
  IconButton,
  Checkbox,
  FormGroup,
  Chip,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Region, RegionalConfig } from '@/services/settingsService';

interface RegionalDeliveryFeeConfigProps {
  deliveryFeeConfigs: RegionalConfig<{
    enabled: boolean;
    type: 'flat' | 'perKm';
    flatAmount: number;
    perKmRate: number;
  }>[];
  onChange: (configs: RegionalConfig<{
    enabled: boolean;
    type: 'flat' | 'perKm';
    flatAmount: number;
    perKmRate: number;
  }>[]) => void;
  errors?: Record<string, string>;
  clearError?: (field: string) => void;
}

const AVAILABLE_REGIONS: Region[] = ['Lagos', 'Nairobi', 'Accra', 'All'];

export const RegionalDeliveryFeeConfig: React.FC<RegionalDeliveryFeeConfigProps> = ({
  deliveryFeeConfigs,
  onChange,
  errors = {},
  clearError,
}) => {
  const handleAddConfig = () => {
    onChange([
      ...deliveryFeeConfigs,
      {
        regions: [],
        value: {
          enabled: true,
          type: 'flat',
          flatAmount: 5000,
          perKmRate: 100,
        },
      },
    ]);
  };

  const handleRemoveConfig = (index: number) => {
    const newConfigs = deliveryFeeConfigs.filter((_, i) => i !== index);
    onChange(newConfigs);
  };

  const handleRegionToggle = (configIndex: number, region: Region) => {
    const newConfigs = [...deliveryFeeConfigs];
    const config = newConfigs[configIndex];

    if (config.regions.includes(region)) {
      config.regions = config.regions.filter((r) => r !== region);
    } else {
      config.regions = [...config.regions, region];
    }

    onChange(newConfigs);
    clearError?.(`deliveryFee_${configIndex}_regions`);
  };

  const handleValueChange = (
    configIndex: number,
    field: 'enabled' | 'type' | 'flatAmount' | 'perKmRate',
    value: boolean | string | number
  ) => {
    const newConfigs = [...deliveryFeeConfigs];
    newConfigs[configIndex].value = {
      ...newConfigs[configIndex].value,
      [field]: value,
    };
    onChange(newConfigs);
    clearError?.(`deliveryFee_${configIndex}_${field}`);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Delivery Fee Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure region-specific vehicle delivery fees
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddConfig}
            size="small"
          >
            Add Region
          </Button>
        </Box>

        {deliveryFeeConfigs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No delivery fee configurations. Click "Add Region" to create one.
            </Typography>
          </Box>
        )}

        {deliveryFeeConfigs.map((config, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Configuration #{index + 1}
                </Typography>

                {/* Region Selection */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Regions
                  </Typography>
                  <FormGroup row>
                    {AVAILABLE_REGIONS.map((region) => (
                      <FormControlLabel
                        key={region}
                        control={
                          <Checkbox
                            checked={config.regions.includes(region)}
                            onChange={() => handleRegionToggle(index, region)}
                          />
                        }
                        label={region}
                      />
                    ))}
                  </FormGroup>
                  {errors[`deliveryFee_${index}_regions`] && (
                    <Typography variant="caption" color="error">
                      {errors[`deliveryFee_${index}_regions`]}
                    </Typography>
                  )}
                </Box>

                {/* Enable Switch */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.value.enabled}
                      onChange={(e) => handleValueChange(index, 'enabled', e.target.checked)}
                    />
                  }
                  label="Enable delivery fee for these regions"
                  sx={{ mb: 2 }}
                />

                {config.value.enabled && (
                  <>
                    {/* Fee Type */}
                    <Box sx={{ mb: 2 }}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          <Typography variant="body2" fontWeight="medium">
                            Fee Type
                          </Typography>
                        </FormLabel>
                        <RadioGroup
                          value={config.value.type}
                          onChange={(e) => handleValueChange(index, 'type', e.target.value)}
                          row
                        >
                          <FormControlLabel value="flat" control={<Radio />} label="Flat Amount" />
                          <FormControlLabel value="perKm" control={<Radio />} label="Per Kilometer" />
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    {/* Amounts */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Flat Amount
                        </Typography>
                        <TextField
                          type="number"
                          value={config.value.flatAmount}
                          onChange={(e) => handleValueChange(index, 'flatAmount', Number(e.target.value))}
                          inputProps={{ min: 0 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                          }}
                          error={!!errors[`deliveryFee_${index}_flatAmount`]}
                          helperText={errors[`deliveryFee_${index}_flatAmount`] || 'Used when type is "Flat Amount"'}
                          fullWidth
                          size="small"
                          disabled={config.value.type !== 'flat'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Per Kilometer Rate
                        </Typography>
                        <TextField
                          type="number"
                          value={config.value.perKmRate}
                          onChange={(e) => handleValueChange(index, 'perKmRate', Number(e.target.value))}
                          inputProps={{ min: 0 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/km</InputAdornment>,
                          }}
                          error={!!errors[`deliveryFee_${index}_perKmRate`]}
                          helperText={errors[`deliveryFee_${index}_perKmRate`] || 'Used when type is "Per Kilometer"'}
                          fullWidth
                          size="small"
                          disabled={config.value.type !== 'perKm'}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>

              <IconButton
                onClick={() => handleRemoveConfig(index)}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Display selected regions as chips */}
            {config.regions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {config.regions.map((region) => (
                  <Chip key={region} label={region} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </CardContent>
    </Card>
  );
};
